import { debounce } from 'throttle-debounce';

import { $config, $waitForClient } from '@client/core/atoms/config.js';
import { addLifecycleEvent } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import { DirectRenderedSlots } from '@client/core/relevantDigital/DirectRenderedSlots.js';
import * as RelevantDigital from '@client/core/relevantDigital/index.js';
import { debouncedSendMetrics } from '@client/core/services/metrics.js';
import {
  debugLog,
  DisplayPlacementId,
  type PlacementId,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';

import { Api } from './Api/index.js';
import { isLowPerformingSubverticalSection } from './utils/shouldLoadRelevantDigitalSlots.js';

export namespace BatchedSlots {
  let queue = new Array<googletag.Slot>();
  let normalQueue = new Array<googletag.Slot>();
  let immediateQueue = new Array<googletag.Slot>();

  // Track the current active batch controller
  let currentBatchController = new AbortController();

  export const IMMEDIATE_SLOTS = [
    DisplayPlacementId.AdvtTop1,
    DisplayPlacementId.AdvtRight1,
    DisplayPlacementId.AdvtLeft1,
    DisplayPlacementId.AdvtWallpaper,
    DisplayPlacementId.AdvtAudience
  ];

  export function cancelActiveBatches() {
    currentBatchController.abort();
    // Create a fresh controller for the incoming new filter batch
    currentBatchController = new AbortController();

    // Clear the queues so stale slots queued right before the filter change don't leak
    queue = [];
    normalQueue = [];
    immediateQueue = [];
  }

  export namespace SplitBatchQueue {
    export function add(slot: googletag.Slot) {
      const slotId = slot.getSlotElementId();
      const isImmediatePlacement = IMMEDIATE_SLOTS.includes(
        slotId as DisplayPlacementId
      );

      if (isImmediatePlacement) {
        immediateQueue.push(slot);
        SplitBatchImmediateQueue.flush.debounced();
      } else {
        normalQueue.push(slot);
        SplitBatchQueue.flush.debounced();
      }

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) {
            if (isImmediatePlacement && immediateQueue.length > 0) {
              SplitBatchImmediateQueue.flush.debounced();
            } else if (!isImmediatePlacement && normalQueue.length > 0) {
              SplitBatchQueue.flush.debounced();
            }
          }
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...normalQueue];
      const controller = currentBatchController;
      normalQueue = [];

      load(slotsToLoad, controller.signal).catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          debugLog('Batch cancelled due to filter change', { slotsToLoad });
          return;
        }
        debugLog('Failed batched loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, SplitBatchQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace SplitBatchImmediateQueue {
    export function flush() {
      const slotsToLoad = [...immediateQueue];
      const controller = currentBatchController;
      immediateQueue = [];

      load(slotsToLoad, controller.signal).catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          debugLog('Immediate batch cancelled due to filter change', {
            slotsToLoad
          });
          return;
        }
        debugLog('Failed immediate loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates immediate slots and refreshes them after 50ms. */
      export const debounced = debounce(50, SplitBatchImmediateQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace Queue {
    export function add(slot: googletag.Slot) {
      queue.push(slot);
      flush.debounced();

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) flush.debounced();
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...queue];
      const controller = currentBatchController;
      queue = [];

      load(slotsToLoad, controller.signal).catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          debugLog('Queue batch cancelled due to filter change', {
            slotsToLoad
          });
          return;
        }
        debugLog('Failed batched loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, Queue.flush, { atBegin: false });
    }
  }

  export async function load(
    slots: Array<googletag.Slot>,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ) {
    const isAborted = () => abortSignals.some((signal) => signal.aborted);

    if (isAborted()) return;

    const { brand, subvertical, pageType } = $config.get() ?? {};
    const formattedSlotIds = await Api(
      (googletag) => {
        for (const slot of slots) {
          googletag.display(slot);
        }

        return slots.map((slot) => slot.getSlotElementId()).join(', ');
      },
      ...abortSignals
    );

    if (isAborted()) return;

    if (
      !isFeatureEnabled(UNLEASH_FEATURE_NAME.killswitchPrebid) &&
      !isLowPerformingSubverticalSection(brand, pageType, subvertical)
    ) {
      try {
        const auctionTimeout = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Prebid auction timed out after 2000ms'));
          }, 2000);
        });
        await Promise.race([
          RelevantDigital.Slots.load(slots, ...abortSignals),
          auctionTimeout
        ]);
        addLifecycleEvent(`Prebid auction completed for ${formattedSlotIds}`);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          debugLog('Prebid auction aborted', error);
          return;
        }
        debugLog('Prebid auction failed', error);
        addLifecycleEvent(`Prebid auction failed for ${formattedSlotIds}`);
      }
    }

    if (isAborted()) return;

    const slotsToRefresh: Array<googletag.Slot> = [];

    for (const slot of slots) {
      const slotId = slot.getSlotElementId();
      const isDirectRendered = DirectRenderedSlots.consume(slotId);
      if (isDirectRendered) {
        const el = document.getElementById(slotId);
        if (el) el.style.removeProperty('display');
        updatePlacementKeyValueById(slotId as PlacementId, 'status', 'loaded');
        debugLog(`Slot '${slotId}' is directly rendered, skipping GAM refresh`);
      } else {
        DirectRenderedSlots.observe(slotId);
        slotsToRefresh.push(slot);
      }
    }

    if (slotsToRefresh.length > 0) {
      await Api(...abortSignals)
        .pubads()
        .refresh(slotsToRefresh);
    }

    addLifecycleEvent(`GAM Load batch of Ads for ${formattedSlotIds}`);
    debouncedSendMetrics();
  }
}
